import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import useAuthInit from '../../hooks/useAuthInit';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';
import { Plus, Trash2, Upload, Music } from 'lucide-react';

const ALBUM_TYPES = [
  { id: 1, label: '싱글' },
  { id: 2, label: 'EP' },
  { id: 3, label: '정규' },
  { id: 4, label: '컴필레이션' },
];

export default function PublicReviewWritePage() {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const { loading: authLoading } = useAuthInit();

  useEffect(() => {
    if (authLoading) return;
    if (!me) {
      navigate(`${PUBLIC_BASE}/login`, { replace: true });
    } else if (me.userType === 'client') {
      alert('신규 신청은 일반회원만 가능합니다. 권리사는 정산시스템을 이용 해주세요.');
      navigate(`${PUBLIC_BASE}/review`, { replace: true });
    }
  }, [me, authLoading, navigate]);

  // 앨범 정보 (직접 입력)
  const [albumName, setAlbumName] = useState('');
  const [clientName, setClientName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [albumTypeId, setAlbumTypeId] = useState('');
  const [physicalAlbumYn, setPhysicalAlbumYn] = useState('N');
  const [riakId, setRiakId] = useState('');
  const [riakProducerName, setRiakProducerName] = useState('');

  // 곡 정보
  const [songs, setSongs] = useState([]);
  const [showSongs, setShowSongs] = useState(false);
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [currentSongIdx, setCurrentSongIdx] = useState(null);
  const [tempLyrics, setTempLyrics] = useState('');
  const [tempLyricsTranslation, setTempLyricsTranslation] = useState('');
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [payments, setPayments] = useState(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (!lyricsModalOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') closeLyricsModal(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lyricsModalOpen]);

  useEffect(() => {
    if (!me?.id) return;
    async function init() {
      const tossPayments = await loadTossPayments('live_ck_DpexMgkW36RDW5Qab2WB3GbR5ozO');
      const customerKey = `member_${me.id}`;
      const widget = tossPayments.payment({ customerKey });
      setPayments(widget);
    }
    init();
  }, [me?.id]);

  // 앨범 필수필드 검증
  const albumValid = albumName.trim() && clientName.trim() && artistName.trim() && releaseDate && albumTypeId;

  const handleShowSongs = () => {
    if (!albumValid) {
      alert('앨범 정보의 필수 항목을 모두 입력해 주세요.');
      return;
    }
    if (!showSongs) {
      setSongs([createEmptySong(1)]);
      setShowSongs(true);
    }
  };

  function createEmptySong(track) {
    return {
      titleYn: 'N', track, nameKr: '', writerKr: '', composerKr: '', arrangerKr: '',
      featuring: '', featuringLatestRelease: '', performance: '', lyrics: '',
      lyricsTranslation: '', woneumYn: false, fileUrl: null,
    };
  }

  const addSong = () => {
    setSongs((prev) => [...prev, createEmptySong(prev.length + 1)]);
  };

  const removeSong = (idx) => {
    if (songs.length <= 1) return;
    setSongs((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, track: i + 1 })));
  };

  const updateSong = (idx, field, value) => {
    setSongs((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateWoneumYn = (idx, checked) => {
    const count = songs.filter((s) => s.woneumYn).length;
    if (checked && count >= 3) {
      alert('원음방송은 최대 3곡까지 선택할 수 있습니다.');
      return;
    }
    updateSong(idx, 'woneumYn', checked);
  };

  const handleFileUpload = async (idx, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [idx]: true }));
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const res = await axios.post('/api/public/reviewApply/upload-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateSong(idx, 'fileUrl', res.data.fileUrl);
    } catch (e) {
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const openLyricsModal = (idx) => {
    setCurrentSongIdx(idx);
    setTempLyrics(songs[idx]?.lyrics || '');
    const trans = songs[idx]?.lyricsTranslation || '';
    setTempLyricsTranslation(trans);
    setHasTranslation(!!trans);
    setLyricsModalOpen(true);
  };
  const closeLyricsModal = () => {
    setLyricsModalOpen(false); setCurrentSongIdx(null);
    setTempLyrics(''); setTempLyricsTranslation(''); setHasTranslation(false); setTranslating(false);
  };
  const autoTranslate = async () => {
    if (!tempLyrics.trim()) return;
    setHasTranslation(true);
    setTranslating(true);
    try {
      const res = await axios.post('/api/public/reviewApply/translate-lyrics', { lyrics: tempLyrics });
      setTempLyricsTranslation(res.data.translation || '');
    } catch (e) {
      alert('번역 중 오류가 발생했습니다.');
    } finally {
      setTranslating(false);
    }
  };
  const saveLyricsModal = () => {
    if (currentSongIdx !== null) {
      updateSong(currentSongIdx, 'lyrics', tempLyrics);
      updateSong(currentSongIdx, 'lyricsTranslation', hasTranslation ? tempLyricsTranslation : '');
    }
    closeLyricsModal();
  };

  const canSubmit = useMemo(() => {
    if (!albumValid || !songs.length) return false;
    for (const s of songs) {
      if (!s.nameKr?.trim()) return false;
      if (!s.performance?.trim()) return false;
      if (!(s.nameKr || '').includes('Inst.') && !s.lyrics?.trim()) return false;
    }
    const bcCount = songs.filter((s) => s.woneumYn).length;
    return bcCount > 0 && bcCount <= 3;
  }, [albumValid, songs]);

  const onSubmit = async () => {
    if (!canSubmit) { alert('필수 항목을 확인해 주세요.'); return; }
    const orderId = `order_${Date.now()}`;
    const amount = 110000;

    const payload = {
      albumName: albumName.trim(),
      clientName: clientName.trim(),
      artistName: artistName.trim(),
      releaseDate,
      albumTypeId: Number(albumTypeId),
      physicalAlbumYn,
      riakId: riakId.trim(),
      riakProducerName: riakProducerName.trim(),
      songs: songs.map((s) => ({
        titleYn: s.titleYn,
        track: s.track,
        nameKr: s.nameKr?.trim(),
        writerKr: s.writerKr?.trim(),
        composerKr: s.composerKr?.trim(),
        arrangerKr: s.arrangerKr?.trim(),
        featuring: s.featuring?.trim(),
        featuringLatestRelease: s.featuringLatestRelease?.trim(),
        performance: s.performance?.trim(),
        lyrics: s.lyrics?.trim(),
        lyricsTranslation: s.lyricsTranslation?.trim() || null,
        woneumYn: s.woneumYn || false,
        fileUrl: s.fileUrl || null,
      })),
    };

    sessionStorage.setItem('publicReviewApplyPayload', JSON.stringify(payload));

    try {
      await payments.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: amount },
        orderId,
        orderName: 'p!nk 방송심의 서비스',
        successUrl: `${window.location.origin}${PUBLIC_BASE}/review/payment/success?orderId=${orderId}&amount=${amount}`,
        failUrl: `${window.location.origin}${PUBLIC_BASE}/review/payment/fail`,
      });
    } catch (e) {
      console.error(e);
      alert('결제창 열기 중 오류가 발생했습니다.');
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        {/* 안내 */}
        <div className="mb-8 text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-3 text-white tracking-tight">음원심의 신청서 작성</h1>
          <p className="text-sm text-slate-400">
            각 방송사의 사정에 따라 처리기간이 다르므로{' '}
            <span className="text-cyan-500 font-bold">최소한 발매 15일 전</span>에 신청해야 발매 전 심의가 완료될 수 있습니다.
            <br />본 양식에 기입된 내용을 임의로 수정하여 접수하지 않으니, 신중히 기입 부탁드립니다.
          </p>
        </div>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">앨범 정보</div>
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-4 bg-[#182334]">
            <InputField label="앨범명" required value={albumName} onChange={(e) => setAlbumName(e.target.value)} placeholder="앨범명을 입력하세요" />
            <InputField label="권리사명" required value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="권리사명을 입력하세요" />
            <InputField label="아티스트명" required value={artistName} onChange={(e) => setArtistName(e.target.value)} placeholder="아티스트명을 입력하세요" />
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                발매일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                앨범 타입 <span className="text-red-500">*</span>
              </label>
              <select
                value={albumTypeId}
                onChange={(e) => setAlbumTypeId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
                <option value="">선택하세요</option>
                {ALBUM_TYPES.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
              </select>
            </div>
          </div>

          {/* 앨범 추가 정보 */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">앨범 추가 정보</div>
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-4 bg-[#182334]">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">피지컬앨범 유무</label>
              <div className="flex gap-6">
                {['Y', 'N'].map((v) => (
                  <label key={v} className="flex items-center gap-2 text-sm text-slate-200">
                    <input type="radio" name="physical" value={v} checked={physicalAlbumYn === v} onChange={(e) => setPhysicalAlbumYn(e.target.value)} />
                    {v === 'Y' ? '있음' : '없음'}
                  </label>
                ))}
              </div>
              {physicalAlbumYn === 'Y' && (
                <p className="text-sm text-rose-400 mt-2">
                  피지컬앨범이 있는 경우, 포장이 되어 있고 판매 인지가 붙어있는 <b>유통 음반 27장</b>을 보내주셔야 합니다.<br />※ 소비자가 구매 시 받는 상태와 동일한 앨범
                </p>
              )}
            </div>
            <InputField label="RIAK 아이디" value={riakId} onChange={(e) => setRiakId(e.target.value)} placeholder="예) pink" />
            <InputField label="RIAK 제작사명" value={riakProducerName} onChange={(e) => setRiakProducerName(e.target.value)} placeholder="예) 핑크페이퍼" />
            <p className="text-sm text-rose-400">
              (선택){' '}
              <a href="http://review.riak.or.kr" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-600"><b>review.riak.or.kr</b></a>{' '}
              회원이신 경우, 가입 당시 기입한 아이디/제작사명을 기입 해주세요.
            </p>
          </div>

          {/* 곡 정보 입력하기 버튼 */}
          {!showSongs && (
            <div className="flex justify-center">
              <button
                onClick={handleShowSongs}
                className="flex items-center gap-2 px-6 py-3 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl transition-colors">
                <Music size={18} />
                곡 정보 입력하기
              </button>
            </div>
          )}

          {/* 곡 리스트 */}
          {showSongs && (
            <>
              <div className="flex items-center my-10">
                <div className="flex-grow border-t border-white/[0.08]" />
                <span className="px-3 text-sm font-semibold text-slate-500">곡 정보</span>
                <div className="flex-grow border-t border-white/[0.08]" />
              </div>

              <div className="text-sm text-slate-500 mb-3">
                <ul>
                  <li className="mb-3">
                    저작권법에서는 저작물(음반)에 대한 저작자(작곡자, 작사자)와 실연자(가수, 연주자, 지휘자)의 성명을 표시하도록 하고 있습니다.<br />
                    음반에 대한 저작자와 실연자의 성명 또는 예명을 기재하지 않을 경우 이들의 성명표시권 침해가 되어 법적 분쟁이 발생될 수 있으니{' '}
                    <span className="text-cyan-500">작곡자, 작사자, 편곡자, 피쳐링, 가수와 곡연주자(연주악기명)의 성명 또는 예명</span>을 필히 기재하여 주시기 바랍니다.
                  </li>
                  <li className="text-rose-400">
                    원음방송의 경우, 각 앨범당 최대 3곡만 심의를 받고 있습니다. <span className="font-bold">원음방송 심의를 위한 3곡 표시</span>를 해주세요.
                    <br />(3곡 이하의 앨범은 표시하지 않으셔도 됩니다)
                  </li>
                </ul>
              </div>

              <div className="space-y-6">
                {songs.map((s, idx) => (
                  <div key={idx} className="border border-white/[0.08] rounded-2xl p-4 space-y-3 bg-[#182334] relative">
                    {songs.length > 1 && (
                      <button onClick={() => removeSong(idx)} className="absolute top-3 right-3 p-1.5 text-slate-500 hover:text-rose-400 transition-colors" title="곡 삭제">
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="text-sm font-semibold text-sky-400 mb-2">Track {s.track}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">타이틀 YN</label>
                        <select value={s.titleYn} onChange={(e) => updateSong(idx, 'titleYn', e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300">
                          <option value="N">N</option>
                          <option value="Y">Y</option>
                        </select>
                      </div>
                      <InputField label="곡명" required value={s.nameKr} onChange={(e) => updateSong(idx, 'nameKr', e.target.value)} placeholder="곡명을 입력하세요" />
                      <InputField label="작사" value={s.writerKr} onChange={(e) => updateSong(idx, 'writerKr', e.target.value)} placeholder="작사자" />
                      <InputField label="작곡" value={s.composerKr} onChange={(e) => updateSong(idx, 'composerKr', e.target.value)} placeholder="작곡자" />
                      <InputField label="편곡" value={s.arrangerKr} onChange={(e) => updateSong(idx, 'arrangerKr', e.target.value)} placeholder="편곡자" />
                    </div>

                    {/* 음원 파일 업로드 */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">음원 파일</label>
                      {s.fileUrl ? (
                        <div className="space-y-2">
                          <audio controls src={s.fileUrl} className="w-full" />
                          <button onClick={() => updateSong(idx, 'fileUrl', null)} className="text-xs text-rose-400 hover:text-rose-300">파일 변경</button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/[0.15] rounded-xl cursor-pointer hover:bg-white/5 transition-colors">
                          {uploading[idx] ? (
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <div className="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                              업로드 중...
                            </div>
                          ) : (
                            <>
                              <Upload size={16} className="text-slate-500" />
                              <span className="text-sm text-slate-400">음원 파일 선택 (MP3, WAV, FLAC)</span>
                            </>
                          )}
                          <input type="file" accept="audio/*" className="hidden" onChange={(e) => handleFileUpload(idx, e.target.files[0])} />
                        </label>
                      )}
                    </div>

                    <InputField label="피처링" value={s.featuring} onChange={(e) => updateSong(idx, 'featuring', e.target.value)} placeholder="예) 홍길동" />
                    <InputField label="피처링 아티스트의 최근 음원" value={s.featuringLatestRelease} onChange={(e) => updateSong(idx, 'featuringLatestRelease', e.target.value)} placeholder="예) 홍길동 - 동해번쩍 서해번쩍" />
                    <InputField label="실연(연주자명/악기명)" required value={s.performance} onChange={(e) => updateSong(idx, 'performance', e.target.value)} placeholder="예) 홍길동/기타, 김철수/드럼" />

                    {/* 가사 */}
                    {!(s.nameKr || '').includes('Inst.') && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                          가사 <span className="text-red-500">*</span>
                        </label>
                        <button
                          onClick={() => openLyricsModal(idx)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            s.lyrics ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                          }`}>
                          {s.lyrics ? '가사 수정' : '가사 입력'}
                        </button>
                      </div>
                    )}

                    {/* 원음방송 */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={s.woneumYn} onChange={(e) => updateWoneumYn(idx, e.target.checked)} className="peer hidden" />
                      <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white text-sm">✓</span>
                      <span className="text-sm font-semibold text-slate-200">원음방송 선택 (최대 3곡) <span className="text-red-500">*</span></span>
                    </label>
                  </div>
                ))}

                {/* 곡 추가 버튼 */}
                <button onClick={addSong} className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-white/[0.15] rounded-2xl text-sm text-slate-400 hover:text-sky-400 hover:border-sky-400/30 transition-colors">
                  <Plus size={16} />
                  곡 추가
                </button>
              </div>

              {/* 결제 */}
              <div className="flex items-center justify-end gap-3 mt-8">
                <span className="text-2xl font-bold text-sky-500">₩110,000</span>
                <button
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    canSubmit ? 'bg-sky-600 hover:bg-sky-500 text-white' : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  }`}>
                  결제하기
                </button>
                <button onClick={() => navigate(`${PUBLIC_BASE}/review`)} className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                  목록
                </button>
              </div>

              {/* 약관 */}
              <div className="text-sm text-slate-500 mt-6">
                <p>본 서비스는 심의 신청 접수 완료 후, 평균 <span className="text-rose-400">2주 이내</span>에 방송사로 전달되며, 각 방송사의 사정에 따라 심의 기간은 달라질 수 있습니다.</p>
                <p>신청 후 7일 이내로 게시판 접수를 통해 <span className="text-rose-400">환불이 가능</span>하며, 신청 정보 오류에 의한 <span className="text-rose-400">수정/재접수는 별도 문의</span> 바랍니다.</p>
                <p>검토가 완료되어 심의가 시작된 이후에는 <span className="text-rose-400">환불이 불가</span>합니다.</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 가사 모달 */}
      {lyricsModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50" onClick={closeLyricsModal}>
          <div className="bg-[#182334] rounded-2xl p-6 w-3/4 h-3/4 flex flex-col shadow-xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-white mb-2">가사 입력</h2>
            <p className="text-sm text-rose-400 mb-2">※ 영문 가사의 경우 <b>의역이 필수</b>입니다. ex) I want you only (난 너만 있으면 돼)</p>
            <textarea
              value={tempLyrics}
              onChange={(e) => setTempLyrics(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none ${hasTranslation ? 'flex-none h-2/5' : 'flex-1'}`}
              placeholder="곡의 전체 가사를 입력해 주세요."
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input type="checkbox" id="hasTranslationCheck" checked={hasTranslation} onChange={(e) => setHasTranslation(e.target.checked)} className="w-4 h-4 accent-sky-400" />
              <label htmlFor="hasTranslationCheck" className="text-sm text-slate-300 cursor-pointer">한글번역 포함</label>
              <button onClick={autoTranslate} disabled={translating || !tempLyrics.trim()}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {translating ? '번역 중...' : '✦ 자동번역'}
              </button>
            </div>
            {hasTranslation && (
              <textarea
                value={translating ? '번역 중...' : tempLyricsTranslation}
                onChange={(e) => setTempLyricsTranslation(e.target.value)}
                readOnly={translating}
                className="flex-1 w-full mt-2 px-3 py-2 rounded-xl border border-sky-500/30 bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                placeholder="한글번역 가사를 입력해 주세요."
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">취소</button>
              <button onClick={saveLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors">저장</button>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function InputField({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text" value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
      />
    </div>
  );
}
